import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import BaseButton from "../BaseButton";
import { Close, Loading } from "../../Constant/index";
import { X } from "react-feather";

let openModalsCount = 0;
let originalOverflow = "";

const BaseModal = ({
  isOpen,
  toggler,
  close,
  onSecondaryClick,
  title,
  children,
  bodyClass,
  submit,
  submitText,
  closeText,
  disabled,
  loader,
  hideClose,
  hideSubmit,
  hasSubmitButton,
  size,
  dialogClass,
}) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 0);
    return () => clearTimeout(timer);
  }, []);
  /* Lock body scroll while open — cleans up reliably on close or unmount */
  useEffect(() => {
    if (!isOpen) return;
    if (openModalsCount === 0) {
      originalOverflow = document.body.style.overflow || "";
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    }
    openModalsCount++;
    return () => {
      openModalsCount = Math.max(0, openModalsCount - 1);
      if (openModalsCount === 0) {
        document.body.style.overflow = originalOverflow;
        document.documentElement.style.overflow = "";
      }
    };
  }, [isOpen]);

  /* Close on Escape — always just dismisses, never triggers secondary action */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && isOpen) toggler?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, toggler]);

  if (!isOpen) return null;

  const sizeMap = { sm: "400px", lg: "720px", xl: "900px" };
  const maxWidth = sizeMap[size] || "560px";

  if (!isMounted) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="base-modal-title"
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        position: "fixed",
        top: "var(--navbar-height, 64px)",
        left: 0,
        width: "100vw",
        height: "calc(100vh - var(--navbar-height, 64px))",
        zIndex: 1055,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "24px 16px",
        boxSizing: "border-box",
      }}
    >
      {/* Backdrop — always just dismisses */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "var(--modal-overlay)",
          backdropFilter: "blur(4px)",
        }}
        onClick={toggler}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div
        className={`modal-dialog animate-scale-in ${dialogClass || ""}`}
        style={{
          position: "relative",
          width: "100%",
          maxWidth,
          display: "flex",
          flexDirection: "column",
          margin: "auto",
          maxHeight: "calc(100vh - var(--navbar-height, 64px) - 48px)",
          overflow: "hidden",
        }}
      >
        <div
          className="modal-content"
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            zIndex: 1056,
            height: "100%",
          }}
        >
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title" id="base-modal-title">
              {title}
            </h5>
            <button
              type="button"
              onClick={toggler}
              aria-label="Close"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: "1px solid var(--glass-border)",
                background: "var(--icon-btn-bg)",
                color: "var(--text-secondary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(244,63,94,0.1)";
                e.currentTarget.style.color = "#f43f5e";
                e.currentTarget.style.borderColor = "rgba(244,63,94,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--icon-btn-bg)";
                e.currentTarget.style.color = "var(--text-secondary)";
                e.currentTarget.style.borderColor = "var(--glass-border)";
              }}
            >
              <X size={15} strokeWidth={2.5} />
            </button>
          </div>

          {/* Body */}
          <div
            className={`modal-body ${bodyClass || ""}`}
            style={{
              overflow: "auto",
              overflowX: "hidden",
              flex: "1 1 auto",
              minHeight: 0,
              position: "relative",
            }}
          >
            {children}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            {!hideClose && (
              <BaseButton
                color="cancel"
                onClick={onSecondaryClick ?? close ?? toggler}
                disabled={disabled}
              >
                {closeText || Close}
              </BaseButton>
            )}
            {hasSubmitButton === undefined && !hideSubmit && (
              <BaseButton color="primary" onClick={submit} disabled={disabled} loader={loader}>
                {loader ? Loading : submitText}
              </BaseButton>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BaseModal;
